import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CodeBlock } from "@/components/Primitives";
import { MentorPanel } from "@/components/MentorPanel";
import { UpgradeGate } from "@/components/TrialBanner";

export default function LessonView() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [busy, setBusy] = useState("");
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/lessons/${slug}`);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      setError(errMsg(e, "Lesson not found"));
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleComplete = async () => {
    setBusy("complete");
    await api.post(`/roadmap/lessons/${slug}/state`, { completed: !data.lesson.completed });
    await load();
    setBusy("");
  };

  const submitExercise = async (exSlug) => {
    setBusy(exSlug);
    setError("");
    try {
      await api.post(`/exercises/${exSlug}/submit`, { content: answers[exSlug] || "" });
      setSubmitted({ ...submitted, [exSlug]: true });
    } catch (e) {
      setError(errMsg(e, "Unable to submit"));
    } finally {
      setBusy("");
    }
  };

  if (locked) return <UpgradeGate testId="lesson-locked" title="This module is locked" message={error} />;
  if (error) return <p data-testid="lesson-error" className="text-sm text-destructive">{error}</p>;
  if (!data) return <div className="grid place-items-center py-24" data-testid="lesson-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const { lesson, module, exercises } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 fade-up" data-testid="lesson-page">
      <article className="lg:col-span-2 space-y-8">
        <div>
          <Link to="/learn" className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-primary transition-colors duration-150" data-testid="back-to-learn">
            <ArrowLeft className="h-3 w-3" /> {module?.title}
          </Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-display font-black tracking-tighter leading-none" data-testid="lesson-title">{lesson.title}</h1>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{lesson.summary}</p>
          <p className="mt-3 font-mono text-xs text-muted-foreground">{lesson.estimatedMinutes} min · {module?.title}</p>
        </div>

        <div>
          <p className="overline text-muted-foreground">objectives</p>
          <ul className="mt-3 space-y-1.5">
            {lesson.objectives.map((o) => (
              <li key={o} className="text-sm flex gap-2"><span className="text-primary font-mono">›</span>{o}</li>
            ))}
          </ul>
        </div>

        <CodeBlock testId="lesson-code" label="reference" code={lesson.codeBlock} />

        <div className="border-l-2 border-[hsl(var(--warning))] pl-5 py-1">
          <p className="overline text-[hsl(var(--warning))] flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> common mistake</p>
          <p className="mt-2 text-sm text-muted-foreground">{lesson.commonMistake}</p>
        </div>

        <div className="border-l-2 border-primary pl-5 py-1">
          <p className="overline text-primary flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5" /> in production</p>
          <p className="mt-2 text-sm text-muted-foreground">{lesson.productionConsideration}</p>
        </div>

        <div className="border border-border p-5">
          <p className="overline text-muted-foreground">knowledge check</p>
          <p className="mt-3 text-sm font-medium">{lesson.knowledgeCheck.question}</p>
          <details className="mt-3" data-testid="knowledge-check">
            <summary className="font-mono text-xs text-primary cursor-pointer">reveal answer</summary>
            <p className="mt-2 text-sm text-muted-foreground">{lesson.knowledgeCheck.answer}</p>
          </details>
        </div>

        {exercises?.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-bold tracking-tight">Exercises</h2>
            {exercises.map((ex) => (
              <div key={ex.slug} data-testid={`exercise-${ex.slug}`} className="border border-border p-5">
                <p className="font-display font-bold tracking-tight">{ex.title}</p>
                <p className="mt-2 text-sm text-muted-foreground">{ex.prompt}</p>
                <p className="mt-2 font-mono text-xs text-muted-foreground">hint: {ex.hint}</p>
                <Textarea
                  data-testid={`exercise-input-${ex.slug}`}
                  rows={5}
                  className="mt-4 rounded-none font-mono text-xs"
                  placeholder="Your solution…"
                  value={answers[ex.slug] || ""}
                  onChange={(e) => setAnswers({ ...answers, [ex.slug]: e.target.value })}
                />
                <div className="mt-3 flex items-center gap-3">
                  <Button
                    size="sm"
                    className="rounded-none gap-2"
                    disabled={busy === ex.slug || (answers[ex.slug] || "").trim().length < 10}
                    onClick={() => submitExercise(ex.slug)}
                    data-testid={`exercise-submit-${ex.slug}`}
                  >
                    {busy === ex.slug && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit answer
                  </Button>
                  {(submitted[ex.slug] || ex.submitted) && (
                    <span data-testid={`exercise-done-${ex.slug}`} className="font-mono text-xs text-[hsl(var(--success))] flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" /> submitted
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-border pt-6">
          <Button
            className="rounded-none gap-2"
            variant={lesson.completed ? "outline" : "default"}
            onClick={toggleComplete}
            disabled={busy === "complete"}
            data-testid="lesson-complete-btn"
          >
            {busy === "complete" && <Loader2 className="h-4 w-4 animate-spin" />}
            {lesson.completed ? "Mark as not complete" : "Mark lesson complete"}
          </Button>
        </div>
      </article>

      <aside className="space-y-6">
        <MentorPanel topic={`${module?.title}: ${lesson.title}`} lessonSlug={lesson.slug} />
      </aside>
    </div>
  );
}
